import { useEffect, useState } from 'react';
import { settingsService, PlanUsage } from '@/services/settings.service';

export interface SubscriptionState {
  loading: boolean;
  data: PlanUsage | null;
  status: 'active' | 'trial' | 'suspended' | 'expired' | 'unknown';
  expiresAt: Date | null;
  daysRemaining: number | null;
  refresh: () => Promise<void>;
}

let cache: PlanUsage | null = null;
const listeners = new Set<(d: PlanUsage | null) => void>();

function broadcast(d: PlanUsage | null) {
  cache = d;
  listeners.forEach((l) => l(d));
}

export async function refreshSubscription(): Promise<void> {
  try {
    const d = await settingsService.plan();
    broadcast(d);
  } catch {
    // 403 SUBSCRIPTION_EXPIRED is fine — interceptor will route. Don't crash here.
  }
}

export function useSubscription(): SubscriptionState {
  const [data, setData] = useState<PlanUsage | null>(cache);
  const [loading, setLoading] = useState<boolean>(cache === null);

  useEffect(() => {
    const onChange = (d: PlanUsage | null) => setData(d);
    listeners.add(onChange);
    if (!cache) {
      setLoading(true);
      refreshSubscription().finally(() => setLoading(false));
    }
    return () => { listeners.delete(onChange); };
  }, []);

  const status = (data?.company.status as SubscriptionState['status']) ?? 'unknown';
  const expiresAt = data?.company.expiresAt ? new Date(data.company.expiresAt) : null;
  const daysRemaining = expiresAt
    ? Math.max(0, Math.ceil((expiresAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;

  return { loading, data, status, expiresAt, daysRemaining, refresh: refreshSubscription };
}
